/**
 * FASE E.3 · TURNO 2C — Premium Report page (lawyer / professional).
 *
 * End-to-end operativa: form intestazione professionale (15 campi +
 * registrations dinamiche + preview), validazione (`first_name`,
 * `last_name`, `professional_role`), salvataggio via PUT /api/cases/{id}
 * (`premium_professional_header`), generazione via
 * POST /api/cases/{id}/premium-pdf (T2A), download blob.
 * Lawyer: opzione facoltativa "confronto tecnico gratuito" con
 * confirmation esplicito.
 * Professional: NESSUNA opzione consulente.
 * Private: redirect immediato alla Dashboard.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, FileDown, CheckCircle2, Loader2, Info } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getCase, updateCase, requestConsultation, api } from "@/lib/api";

const EMPTY_HEADER = {
  professional_title: "", first_name: "", last_name: "",
  studio_or_organization: "", professional_role: "",
  registrations: [],
  vat_number: "", phone: "", email: "", pec: "",
  office_address: "", postal_code: "", city: "", province: "",
  country: "Italia", website: "",
};

export default function PremiumPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [header, setHeader] = useState(EMPTY_HEADER);
  const [step, setStep] = useState(1); // 1=header, 2=lawyer-consult, 3=ready
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [consultChoice, setConsultChoice] = useState(""); // "" | "solo" | "confronto"
  const [showConsultConfirm, setShowConsultConfirm] = useState(false);
  const [consultSubmitting, setConsultSubmitting] = useState(false);
  const [consultRequested, setConsultRequested] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await getCase(id);
        setCaseData(c);
        const ut = (c.effective_user_type || c.user_type || "private").toLowerCase();
        if (ut === "private") {
          toast.error("Il Report Premium non è disponibile per l'utenza privato.");
          nav(`/case/${id}`);
          return;
        }
        if (c.premium_professional_header) {
          setHeader({ ...EMPTY_HEADER, ...c.premium_professional_header,
                       registrations: c.premium_professional_header.registrations || [] });
        }
        if (c.consultant_sharing) setConsultRequested(true);
      } catch {
        toast.error("Impossibile caricare il fascicolo");
      }
    })();
  }, [id, nav]);

  const userType = (caseData?.effective_user_type || caseData?.user_type || "private").toLowerCase();
  const isLawyer = userType === "lawyer";
  const missingFields = useMemo(() => {
    const req = ["first_name", "last_name", "professional_role"];
    return req.filter((k) => !(header[k] || "").trim());
  }, [header]);
  const canProceed = missingFields.length === 0;

  const setField = (k, v) => setHeader((h) => ({ ...h, [k]: v }));
  const addReg = () => setHeader((h) => ({ ...h,
    registrations: [...(h.registrations || []),
                     { type: "", institution: "", registration_number: "", free_text: "" }] }));
  const rmReg = (i) => setHeader((h) => ({ ...h,
    registrations: h.registrations.filter((_, idx) => idx !== i) }));
  const setReg = (i, k, v) => setHeader((h) => ({ ...h,
    registrations: h.registrations.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));

  const saveHeader = async () => {
    if (!canProceed) {
      toast.error("Completa i dati obbligatori dell'intestazione professionale.");
      return false;
    }
    setSaving(true);
    try {
      await updateCase(id, { premium_professional_header: header });
      toast.success("Intestazione professionale salvata");
      setSaving(false);
      return true;
    } catch (e) {
      toast.error("Errore nel salvataggio dell'intestazione");
      setSaving(false);
      return false;
    }
  };

  const generate = async () => {
    if (!canProceed) return;
    setGenerating(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backend}/api/cases/${id}/premium-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ header }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        if (resp.status === 422) {
          toast.error("Completa i dati obbligatori dell'intestazione: "
                       + (data?.detail?.missing_fields || []).join(", "));
        } else if (resp.status === 403) {
          toast.error("Il Report Premium non è disponibile per la tua utenza.");
        } else {
          toast.error("Errore nella generazione del Report Premium");
        }
        setGenerating(false);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setStep(3);
      toast.success("Report Premium pronto");
    } catch (e) {
      toast.error("Errore nella generazione del Report Premium");
    } finally {
      setGenerating(false);
    }
  };

  const requestConsultationAction = async () => {
    setConsultSubmitting(true);
    try {
      // FASE E.3 · TURNO 2D — dedicated atomic endpoint. Replaces the
      // previous generic PUT-based flag write, which was not idempotent,
      // did not surface the case in the CRM, and did not notify the
      // consultant.
      const resp = await requestConsultation(id);
      setConsultRequested(true);
      setShowConsultConfirm(false);
      if (resp?.already_requested) {
        toast.success("Richiesta di confronto tecnico già registrata");
      } else {
        toast.success("Richiesta di confronto tecnico inviata");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message) || "Errore nell'invio della richiesta";
      toast.error(msg);
    } finally {
      setConsultSubmitting(false);
    }
  };

  const proceedFromHeader = async () => {
    const ok = await saveHeader();
    if (!ok) return;
    if (isLawyer) setStep(2);
    else generate();
  };

  const proceedFromConsult = async () => {
    if (consultChoice === "confronto" && !consultRequested) {
      setShowConsultConfirm(true);
      return;
    }
    generate();
  };

  const preview = (
    <div className="bg-slate-50 border hairline p-4 rounded-sm text-sm">
      <div className="overline text-navy mb-2">Anteprima intestazione</div>
      {(header.professional_title || header.first_name || header.last_name) && (
        <div className="font-serif font-semibold uppercase">
          {[header.professional_title, header.first_name, header.last_name].filter(Boolean).join(" ")}
        </div>
      )}
      {header.studio_or_organization && <div>{header.studio_or_organization}</div>}
      {header.professional_role && <div className="text-slate-600">{header.professional_role}</div>}
      {(header.registrations || []).map((r, i) => (
        <div key={i} className="text-xs text-slate-600 mt-1">
          {[r.type, r.institution, r.registration_number].filter(Boolean).join(" · ")}
          {r.free_text && ` · ${r.free_text}`}
        </div>
      ))}
      {header.vat_number && <div className="text-xs text-slate-600 mt-1">P. IVA {header.vat_number}</div>}
      {(header.phone || header.email || header.pec) && (
        <div className="text-xs text-slate-600">
          {[header.phone, header.email, header.pec].filter(Boolean).join(" · ")}
        </div>
      )}
      {(header.office_address || header.city) && (
        <div className="text-xs text-slate-600">
          {[header.office_address, [header.postal_code, header.city].filter(Boolean).join(" "), header.province].filter(Boolean).join(" · ")}
        </div>
      )}
    </div>
  );

  if (!caseData) return <div className="p-8"><Loader2 className="animate-spin inline" /> Caricamento…</div>;

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Header />
      <main className="flex-1 container mx-auto px-4 lg:px-6 py-8 max-w-4xl" data-testid="premium-page">
        <button onClick={() => nav(`/case/${id}`)} className="text-sm text-slate-600 hover:text-navy inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> Torna al fascicolo
        </button>
        <div className="flex items-center gap-2">
          <div className="overline text-navy">Report Premium</div>
          <span
            data-testid="premium-beta-badge"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-forensic-green/10 text-forensic-green border border-forensic-green/30 px-2 py-0.5 rounded-full">
            Accesso Beta — gratuito
          </span>
        </div>
        <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-ink mt-1">
          Genera un report professionale con la tua intestazione
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Il Report Premium viene generato con la tua identità professionale.
          I dati inseriti sono dichiarati dall'utente e non sono verificati automaticamente da Perizia Ready.
        </p>
        <p
          data-testid="premium-beta-microcopy"
          className="text-sm text-forensic-green mt-2 max-w-2xl">
          In questa fase di test, puoi generare gratuitamente il Report Premium professionale.
        </p>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mt-6 text-xs">
          {[
            { k: 1, l: "Intestazione" },
            ...(isLawyer ? [{ k: 2, l: "Confronto tecnico" }] : []),
            { k: 3, l: "Report pronto" },
          ].map((s, i, arr) => (
            <span key={s.k} className={`flex items-center gap-2 ${step >= s.k ? "text-navy font-semibold" : "text-slate-400"}`}>
              <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${step >= s.k ? "border-navy bg-navy text-white" : "border-slate-300"}`}>{s.k}</span>
              {s.l}
              {i < arr.length - 1 && <span className="mx-2 text-slate-300">→</span>}
            </span>
          ))}
        </div>

        {step === 1 && (
          <section className="mt-8 grid lg:grid-cols-3 gap-6" data-testid="premium-header-form">
            <div className="lg:col-span-2 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Titolo" value={header.professional_title} onChange={(v) => setField("professional_title", v)} placeholder="AVV." testid="pph-title" />
                <Field label="Nome *" value={header.first_name} onChange={(v) => setField("first_name", v)} testid="pph-first-name" required />
                <Field label="Cognome *" value={header.last_name} onChange={(v) => setField("last_name", v)} testid="pph-last-name" required />
              </div>
              <Field label="Studio / Organizzazione" value={header.studio_or_organization} onChange={(v) => setField("studio_or_organization", v)} testid="pph-studio" />
              <Field label="Ruolo professionale *" value={header.professional_role} onChange={(v) => setField("professional_role", v)} testid="pph-role" required />

              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className="text-sm font-medium text-ink">Iscrizioni / albi</div>
                  <button onClick={addReg} data-testid="pph-add-registration" className="text-xs text-navy hover:underline inline-flex items-center gap-1">
                    <Plus size={12} /> Aggiungi iscrizione
                  </button>
                </div>
                {(header.registrations || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 border hairline p-2 rounded-sm mb-2" data-testid={`pph-reg-${i}`}>
                    <input value={r.type || ""} onChange={(e) => setReg(i, "type", e.target.value)} placeholder="Tipo" className="input text-sm" />
                    <input value={r.institution || ""} onChange={(e) => setReg(i, "institution", e.target.value)} placeholder="Ente" className="input text-sm" />
                    <input value={r.registration_number || ""} onChange={(e) => setReg(i, "registration_number", e.target.value)} placeholder="Numero" className="input text-sm" />
                    <div className="flex gap-1">
                      <input value={r.free_text || ""} onChange={(e) => setReg(i, "free_text", e.target.value)} placeholder="Note" className="input text-sm flex-1" />
                      <button onClick={() => rmReg(i)} data-testid={`pph-reg-remove-${i}`} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="P. IVA" value={header.vat_number} onChange={(v) => setField("vat_number", v)} testid="pph-vat" />
                <Field label="Telefono" value={header.phone} onChange={(v) => setField("phone", v)} testid="pph-phone" />
                <Field label="Email" value={header.email} onChange={(v) => setField("email", v)} testid="pph-email" />
                <Field label="PEC" value={header.pec} onChange={(v) => setField("pec", v)} testid="pph-pec" />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <Field className="col-span-2" label="Indirizzo" value={header.office_address} onChange={(v) => setField("office_address", v)} testid="pph-address" />
                <Field label="CAP" value={header.postal_code} onChange={(v) => setField("postal_code", v)} testid="pph-postal" />
                <Field label="Città" value={header.city} onChange={(v) => setField("city", v)} testid="pph-city" />
                <Field label="Provincia" value={header.province} onChange={(v) => setField("province", v)} testid="pph-province" />
                <Field label="Paese" value={header.country} onChange={(v) => setField("country", v)} testid="pph-country" />
                <Field className="col-span-2" label="Sito web" value={header.website} onChange={(v) => setField("website", v)} testid="pph-website" />
              </div>
              {!canProceed && (
                <div className="text-xs text-red-600 flex items-center gap-1" data-testid="pph-validation-error">
                  <Info size={12} /> Completa i dati obbligatori dell'intestazione professionale.
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={saveHeader} disabled={saving} data-testid="pph-save-btn" className="border-2 border-navy text-navy hover:bg-navy hover:text-white px-4 py-2 rounded-sm text-sm inline-flex items-center gap-1 disabled:opacity-50">
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Salva intestazione
                </button>
                <button onClick={proceedFromHeader} disabled={saving || !canProceed} data-testid="pph-proceed-btn" className="bg-forensic-green text-white hover:bg-forensic-green/90 px-4 py-2 rounded-sm text-sm inline-flex items-center gap-1 disabled:opacity-50">
                  {isLawyer ? "Prosegui →" : "Genera gratuitamente il Report Premium →"}
                </button>
              </div>
            </div>
            <aside className="lg:sticky lg:top-4 h-fit">{preview}</aside>
          </section>
        )}

        {step === 2 && isLawyer && (
          <section className="mt-8 max-w-2xl" data-testid="lawyer-consult-step">
            <h2 className="font-serif text-2xl font-semibold text-ink">Vuoi anche un confronto tecnico gratuito?</h2>
            <p className="text-sm text-slate-600 mt-2">
              Se lo desideri, puoi condividere il fascicolo con il Dott. Alessio Lugliè
              e richiedere un confronto tecnico gratuito. La condivisione è facoltativa
              e non è necessaria per ottenere il Report Premium.
            </p>
            <div className="mt-4 space-y-3">
              {[
                { v: "solo", t: "Solo Report Premium", d: "Genera e scarica il report con la tua intestazione professionale, senza condividere il fascicolo." },
                { v: "confronto", t: "Report Premium + confronto tecnico gratuito", d: "Genera il report e condividi il fascicolo con il Dott. Alessio Lugliè per il confronto tecnico gratuito." },
              ].map((o) => (
                <label key={o.v} data-testid={`lawyer-consult-opt-${o.v}`}
                       className={`block cursor-pointer border-2 p-4 rounded-sm ${consultChoice === o.v ? "border-navy bg-navy/5" : "border-slate-200 bg-white"}`}>
                  <input type="radio" name="consult" value={o.v} checked={consultChoice === o.v} onChange={() => setConsultChoice(o.v)} className="sr-only" />
                  <div className="font-semibold text-ink">{o.t}</div>
                  <p className="text-sm text-slate-600 mt-1">{o.d}</p>
                </label>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(1)} className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 px-4 py-2 rounded-sm text-sm">← Indietro</button>
              <button onClick={proceedFromConsult} disabled={!consultChoice || generating} data-testid="lawyer-consult-proceed" className="bg-forensic-green text-white hover:bg-forensic-green/90 px-4 py-2 rounded-sm text-sm disabled:opacity-50">
                {generating ? <Loader2 className="animate-spin inline" size={14} /> : "Genera gratuitamente il Report Premium →"}
              </button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="mt-8 max-w-2xl border-2 border-forensic-green bg-forensic-green/5 p-6 rounded-sm" data-testid="premium-ready">
            <CheckCircle2 className="text-forensic-green mb-2" size={32} />
            <h2 className="font-serif text-2xl font-semibold text-ink">Report Premium pronto</h2>
            <p className="text-sm text-slate-700 mt-2">
              Il report è stato generato con la tua intestazione professionale.
            </p>
            {consultRequested && isLawyer && (
              <p className="text-sm text-slate-700 mt-2" data-testid="consult-requested-badge">
                <strong>Confronto tecnico richiesto</strong> · Il fascicolo è stato condiviso con il consulente per il confronto tecnico gratuito.
              </p>
            )}
            <div className="flex gap-3 mt-6">
              <a href={pdfUrl} download={`perizia-ready-premium-${id.slice(0, 8)}.pdf`} data-testid="premium-download-btn"
                 className="bg-navy text-white hover:bg-navy-dark px-5 py-3 rounded-sm text-sm inline-flex items-center gap-2 font-semibold">
                <FileDown size={16} /> Scarica il Report Premium
              </a>
              <button onClick={() => nav(`/case/${id}`)} className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 px-4 py-2 rounded-sm text-sm">
                Torna al fascicolo
              </button>
            </div>
          </section>
        )}

        {/* Lawyer consultation confirmation modal */}
        {showConsultConfirm && (
          <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="lawyer-consult-confirm-modal">
            <div className="bg-white border hairline max-w-lg w-full p-6">
              <h3 className="font-serif text-xl font-semibold">Richiedere il confronto tecnico gratuito?</h3>
              <p className="text-sm text-slate-700 mt-3">
                Con questa scelta il fascicolo sarà trasmesso al Dott. Alessio Lugliè
                affinché possa esaminarlo e ricontattarti per il confronto tecnico gratuito richiesto.
              </p>
              <div className="flex gap-3 justify-end mt-6">
                <button onClick={() => setShowConsultConfirm(false)} data-testid="lawyer-consult-cancel"
                        className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 px-4 py-2 rounded-sm text-sm">Annulla</button>
                <button onClick={async () => { await requestConsultationAction(); await generate(); }}
                        disabled={consultSubmitting}
                        data-testid="lawyer-consult-confirm"
                        className="bg-navy text-white hover:bg-navy-dark px-4 py-2 rounded-sm text-sm disabled:opacity-50">
                  {consultSubmitting ? <Loader2 className="animate-spin inline" size={14} /> : "Conferma e richiedi il confronto"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, placeholder, className = "", testid, required = false }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs uppercase tracking-wider text-slate-600 mb-1">{label}</div>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        data-testid={testid}
        className={`w-full border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-navy ${required && !(value || "").trim() ? "border-red-300 bg-red-50" : "border-slate-300"}`}
      />
    </label>
  );
}
